// useCookieConsent — cookie-consent gate for the marketing site.
//
// Stemne sets no tracking cookies of its own. The only thing that needs
// consent is the Google Ads conversion tag, and that tag lives exclusively
// on the marketing apex (stemne.no) — so this composable is deliberately
// scoped to isMarketingHost(). On a tenant subdomain it is an inert no-op:
// no localStorage read, no localStorage write, no dataLayer push, no
// banner. An end customer booking a meeting at {slug}.stemne.no is never
// asked about cookies because nothing on that host sets one, and asking
// would put a Stemne consent dialog on the operator's own booking page.
//
// Google Consent Mode v2 is the GDPR mechanism. The *default* signal
// denies every storage type and is pushed at module-eval time — before any
// tag script can exist. That ordering is the whole point: gtag's shim is a
// function that queues its arguments onto window.dataLayer, and gtag.js
// replays the queue when it eventually loads, so the deny can (and must)
// fire without loading anything. The Google Ads account is separately
// configured with "tag data marked as consent not given", so an
// un-consented visit records nothing even if the tag were present.

import { computed, ref } from 'vue'
import { isMarketingHost } from '@/utils/platformHost'

export type CookieConsent = 'unset' | 'granted' | 'denied'

const STORAGE_KEY = 'stemne-cookie-consent'

type ConsentState = 'granted' | 'denied'

/** The four Consent Mode v2 storage types gtag reads. */
interface ConsentSignal {
  ad_storage: ConsentState
  ad_user_data: ConsentState
  ad_personalization: ConsentState
  analytics_storage: ConsentState
}

const DENIED: ConsentSignal = {
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  analytics_storage: 'denied',
}

const GRANTED: ConsentSignal = {
  ad_storage: 'granted',
  ad_user_data: 'granted',
  ad_personalization: 'granted',
  analytics_storage: 'granted',
}

interface DataLayerWindow extends Window {
  dataLayer?: unknown[]
}

/** The gtag shim — queue one command onto window.dataLayer.
 *
 *  Google's own snippet is `function gtag(){dataLayer.push(arguments)}`, and
 *  that `arguments` object is NOT an implementation detail we can improve on.
 *  gtag.js identifies commands by testing for a genuine Arguments object; a
 *  real Array is silently skipped. This function used to push an array, on
 *  the stated reasoning that it was "the same array-like shape the tag reads
 *  positionally and is far easier to assert on in a test".
 *
 *  It is not the same shape, and the cost was total: verified against
 *  production on 2026-09-07, the tag made ZERO requests to Google — not the
 *  conversion, not even `config` — while gtag.js sat loaded and idle. Pushing
 *  the identical commands through a canonical `arguments` shim in the same
 *  page fired `pagead/conversion`, `1p-conversion` and `viewthroughconversion`
 *  immediately. The Ads account's conversion_last_received_request_date_time
 *  had stood at 1970-01-01 for the tag's entire life, which is what this
 *  explains.
 *
 *  The IIFE is the point: `arguments` inside it is a real Arguments object
 *  holding exactly the forwarded args. Do not "simplify" it back to an array,
 *  and do not trust a unit test that asserts on array shape to tell you this
 *  works — that is precisely the test that passed for the tag's entire dead
 *  life.
 *
 *  Exported because useGoogleAdsTag queues `js` / `config` through the same
 *  door — there must be exactly one dataLayer writer. */
export function pushGtag(...args: unknown[]): void {
  const win = window as DataLayerWindow
  win.dataLayer = win.dataLayer ?? []
  const queue = win.dataLayer
  function gtag() {
    // biome-ignore lint/complexity/noArguments: gtag.js only processes real Arguments objects; an array is silently ignored (see above)
    queue.push(arguments)
  }
  ;(gtag as (...forwarded: unknown[]) => void)(...args)
}

// active — the marketing apex is the only host that tracks anything.
// Resolved once at module eval: the hostname cannot change without a full
// page load, which re-evaluates this module anyway.
const active = isMarketingHost()

const consent = ref<CookieConsent>('unset')

function readStored(): CookieConsent {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    // Anything that isn't one of the two decisions we write — a truncated
    // value, a stale schema, a key some other tool squatted on — means "we
    // have not asked yet". Never "granted": a corrupt value must not be
    // able to consent on the visitor's behalf.
    return raw === 'granted' || raw === 'denied' ? raw : 'unset'
  } catch {
    // Storage disabled (Safari private mode, hardened browser settings).
    // Treat as undecided and re-ask; the alternative is throwing during
    // module eval and taking the whole app down over a cookie banner.
    return 'unset'
  }
}

function persist(value: ConsentState): void {
  try {
    localStorage.setItem(STORAGE_KEY, value)
  } catch {
    // Consent that can't be persisted is still valid for this page view.
  }
}

if (active) {
  consent.value = readStored()
  // Default-deny first, always, before any other dataLayer traffic.
  pushGtag('consent', 'default', DENIED)
  // A returning visitor who already accepted: lift the default straight
  // away. Without this their session stays denied and the tag we go on to
  // load records nothing — the silent-zero-conversions failure again.
  if (consent.value === 'granted') {
    pushGtag('consent', 'update', GRANTED)
  }
}

function set(value: ConsentState): void {
  if (!active) return
  consent.value = value
  persist(value)
  // An explicit denied update is redundant against the default, but it
  // puts the visitor's actual decision in the dataLayer rather than
  // leaving "denied" ambiguous between "chose no" and "never asked".
  pushGtag('consent', 'update', value === 'granted' ? GRANTED : DENIED)
}

export function useCookieConsent() {
  return {
    consent,
    /** True only where a decision is both needed and still pending. */
    showBanner: computed(() => active && consent.value === 'unset'),
    accept: () => set('granted'),
    decline: () => set('denied'),
  }
}
